import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, FileText, Ruler, Files, HardDrive, Star, Check, Zap, Loader2, ShieldCheck, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { api, getRef, getReward } from "../../lib/api";

const Stars = ({ value, onChange, size = "h-4 w-4", testid }) => (
  <div className="flex items-center gap-0.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        disabled={!onChange}
        onClick={() => onChange && onChange(n)}
        data-testid={onChange ? `star-input-${n}` : undefined}
        className={onChange ? "cursor-pointer" : "cursor-default"}
        aria-label={`${n} star`}
      >
        <Star className={`${size} ${n <= value ? "fill-emerald-400 text-emerald-400" : "text-slate-600"}`} />
      </button>
    ))}
  </div>
);

export const PreviewModal = ({ product, onClose }) => {
  const [buying, setBuying] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ author_name: "", email: "", rating: 5, comment: "", hp: "" });
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const formOpenedRef = useRef(0);

  useEffect(() => {
    if (!product) return;
    setShowForm(false);
    api.reviews(product.id).then(setData).catch(() => setData(null));
  }, [product]);

  const buy = async () => {
    if (product.base_price === 0) {
      toast.success("Free template unlocked", { description: `${product.title} is ready to download.` });
      return;
    }
    setBuying(true);
    try {
      const r = await api.checkout(product.id, window.location.origin, getRef(), getReward());
      if (r.checkout_url) window.location.href = r.checkout_url;
      else toast.success(r.message || "Unlocked");
    } catch {
      toast.error("Could not start checkout");
      setBuying(false);
    }
  };

  const submitReview = async () => {
    if (!form.author_name.trim()) return toast.error("Please add your name");
    if (!form.comment.trim()) return toast.error("Please write a short review");
    setSubmitting(true);
    try {
      await api.createReview({ product_id: product.id, ...form, email: form.email || null, elapsed_ms: Date.now() - (formOpenedRef.current || 0) });
      toast.success("Review posted", { description: "Thanks for sharing your take." });
      setForm({ author_name: "", email: "", rating: 5, comment: "", hp: "" });
      setShowForm(false);
      const fresh = await api.reviews(product.id);
      setData(fresh);
    } catch {
      toast.error("Could not post review");
    } finally {
      setSubmitting(false);
    }
  };

  const avg = data?.count ? data.average : product?.rating;
  const count = data?.count ?? 0;

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="preview-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/80 p-4 backdrop-blur-md"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="relative grid max-h-[90vh] w-full max-w-4xl grid-cols-1 overflow-hidden border border-white/15 bg-slate-900 md:grid-cols-2"
          >
            <button
              data-testid="preview-close-btn"
              onClick={onClose}
              className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center border border-white/15 bg-slate-900/70 text-white backdrop-blur transition-colors duration-200 hover:border-white/40"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="relative hidden md:block">
              <img src={product.image_url} alt={product.title} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-slate-900/80" />
            </div>

            <div className="flex flex-col overflow-y-auto p-8">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">
                {product.software_type} · {product.tier}
              </span>
              <h2 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-white">
                {product.title}
              </h2>
              <div className="mt-2 flex items-center gap-2 text-sm text-slate-400">
                <Stars value={Math.round(avg)} />
                <span data-testid="preview-rating-summary">{avg} · {count} review{count === 1 ? "" : "s"}</span>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-slate-300">{product.description}</p>

              {/* spec grid */}
              <div className="mt-6 grid grid-cols-2 gap-px border border-white/10 bg-white/10">
                {[
                  { icon: FileText, label: "Pages", value: product.page_count },
                  { icon: Ruler, label: "Dimensions", value: product.dimensions },
                  { icon: Files, label: "Formats", value: product.formats?.join(", ") },
                  { icon: HardDrive, label: "File Size", value: product.file_size },
                ].map((s) => (
                  <div key={s.label} className="bg-slate-900 p-4">
                    <s.icon className="mb-2 h-4 w-4 text-emerald-400" />
                    <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                      {s.label}
                    </div>
                    <div className="mt-0.5 text-sm text-white">{s.value}</div>
                  </div>
                ))}
              </div>

              {/* features */}
              <div className="mt-6">
                <p className="mb-2 font-mono text-xs uppercase tracking-[0.15em] text-slate-400">
                  What's inside
                </p>
                <ul className="space-y-1.5">
                  {product.features?.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* reviews */}
              <div className="mt-6 border-t border-white/10 pt-5" data-testid="reviews-section">
                <div className="mb-3 flex items-center justify-between">
                  <p className="font-mono text-xs uppercase tracking-[0.15em] text-slate-400">
                    Trader Reviews
                  </p>
                  <button
                    data-testid="write-review-btn"
                    onClick={() => setShowForm((v) => { const next = !v; if (next) formOpenedRef.current = Date.now(); return next; })}
                    className="flex items-center gap-1.5 border border-white/15 px-2.5 py-1 text-xs text-slate-300 transition-colors duration-200 hover:border-emerald-400 hover:text-white"
                  >
                    <MessageSquarePlus className="h-3.5 w-3.5" /> {showForm ? "Cancel" : "Write a review"}
                  </button>
                </div>

                {showForm && (
                  <div className="mb-4 space-y-3 border border-white/10 bg-slate-950 p-4" data-testid="review-form">
                    <input
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      value={form.hp}
                      onChange={(e) => setForm((f) => ({ ...f, hp: e.target.value }))}
                      style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                    />
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Your rating</span>
                      <Stars value={form.rating} onChange={(n) => setForm((f) => ({ ...f, rating: n }))} testid="review-rating-input" />
                    </div>
                    <input
                      data-testid="review-name-input"
                      value={form.author_name}
                      onChange={(e) => setForm((f) => ({ ...f, author_name: e.target.value }))}
                      placeholder="Your name"
                      className="w-full border border-white/15 bg-slate-900 px-3 py-2 text-sm text-white outline-none placeholder:text-slate-600 focus:border-emerald-400"
                    />
                    <input
                      data-testid="review-email-input"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="Email (optional — verifies your purchase)"
                      className="w-full border border-white/15 bg-slate-900 px-3 py-2 text-sm text-white outline-none placeholder:text-slate-600 focus:border-emerald-400"
                    />
                    <textarea
                      data-testid="review-comment-input"
                      value={form.comment}
                      onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
                      placeholder="Share your take on this template…"
                      rows={3}
                      className="w-full resize-none border border-white/15 bg-slate-900 px-3 py-2 text-sm text-white outline-none placeholder:text-slate-600 focus:border-emerald-400"
                    />
                    <button
                      data-testid="submit-review-btn"
                      onClick={submitReview}
                      disabled={submitting}
                      className="flex items-center gap-2 bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400 disabled:opacity-60"
                    >
                      {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Post review
                    </button>
                  </div>
                )}

                {count === 0 ? (
                  <p data-testid="no-reviews" className="text-sm text-slate-500">
                    No reviews yet — be the first to rate this template.
                  </p>
                ) : (
                  <div className="space-y-3" data-testid="reviews-list">
                    {data.reviews.map((r) => (
                      <div key={r.id} className="border border-white/10 bg-slate-950 p-3" data-testid={`review-${r.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-white">{r.author_name}</span>
                            {r.verified_purchase && (
                              <span className="flex items-center gap-1 border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-emerald-400">
                                <ShieldCheck className="h-3 w-3" /> Verified
                              </span>
                            )}
                          </div>
                          <Stars value={r.rating} size="h-3 w-3" />
                        </div>
                        <p className="mt-1.5 text-sm leading-relaxed text-slate-300">{r.comment}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-8 flex items-center justify-between border-t border-white/10 pt-5">
                <div>
                  <span className="font-mono text-3xl font-bold text-emerald-400">
                    {product.base_price === 0 ? "Free" : `$${product.base_price}`}
                  </span>
                  {product.pricing?.discount_pct > 0 && (
                    <div className="font-mono text-[10px] uppercase tracking-wider text-emerald-500">
                      <Zap className="mb-0.5 mr-1 inline h-3 w-3" />
                      {product.pricing.discount_pct}% below market
                    </div>
                  )}
                </div>
                <button
                  data-testid="preview-buy-btn"
                  onClick={buy}
                  disabled={buying}
                  className="flex items-center gap-2 bg-emerald-500 px-6 py-3 font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400 disabled:opacity-60"
                >
                  {buying && <Loader2 className="h-4 w-4 animate-spin" />}
                  {product.base_price === 0 ? "Get Template" : "Buy Template"}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PreviewModal;
